import moment from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/rootReducer';
import HSTable from '../../../../../common/HSTable';
import { commaSeperator } from '../../../../../utils/validation';
import { Cashflow } from '../../../redux/types';
import { Amount } from '../../../../deals/redux/types';
import { Empty } from 'antd';

const RepaymentTableView: React.FC = () => {
    const data = useSelector((state: RootState) => state.investments.cashflow);

    const currency = useMemo(() => {
        const list = data && data[0];
        return (
            list?.netPayment?.currency ||
            list?.managementFee?.currency ||
            list?.interestPayment?.currency ||
            list?.principalOutstanding?.currency ||
            list?.principalRepayment?.currency ||
            list?.totalAmount?.currency
        );
    }, [data]);

    let columns = [
        {
            title: 'Payment Date',
            width: 200,
            dataIndex: 'paymentDate',
            fixed: 'left',
            render: (text?: { date: string }) => <span>{text ? moment(text.date).format('DD/MM/YYYY') : ''}</span>,
            sorter: (a: Cashflow, b: Cashflow) =>
                moment(a.paymentDate?.date).unix() - moment(b.paymentDate?.date).unix(),
        },
        {
            title: 'Principal Outstanding',
            width: 200,
            dataIndex: 'principalOutstanding',
            render: (text: Amount) => (
                <span>
                    {text?.amountUpto2dp
                        ? `${text.currency} ${commaSeperator(parseFloat(String(text.amountUpto2dp || 0)).toFixed(2))}`
                        : currency
                        ? `${currency} 0.00`
                        : ''}
                </span>
            ),
            sorter: (a: Cashflow, b: Cashflow) =>
                (a.principalOutstanding?.amountUpto2dp || 0) - (b.principalOutstanding?.amountUpto2dp || 0),
        },
        {
            title: 'Interest Payment',
            width: 200,
            dataIndex: 'interestPayment',
            render: (text: Amount) => (
                <span>
                    {text?.amountUpto2dp
                        ? `${text.currency} ${commaSeperator(parseFloat(String(text.amountUpto2dp)).toFixed(2))}`
                        : currency
                        ? `${currency} 0.00`
                        : ''}
                </span>
            ),
            sorter: (a: Cashflow, b: Cashflow) =>
                (a.interestPayment?.amountUpto2dp || 0) - (b.interestPayment?.amountUpto2dp || 0),
        },
        {
            title: 'Principal Repayment',
            dataIndex: 'principalRepayment',
            width: 200,
            render: (text: Amount) => (
                <span>
                    {text?.amountUpto2dp
                        ? `${text.currency} ${commaSeperator(parseFloat(String(text.amountUpto2dp)).toFixed(2))}`
                        : currency
                        ? `${currency} 0.00`
                        : ''}
                </span>
            ),
            sorter: (a: Cashflow, b: Cashflow) =>
                (a.principalRepayment?.amountUpto2dp || 0) - (b.principalRepayment?.amountUpto2dp || 0),
        },
        {
            title: 'Total Payment',
            width: 200,
            dataIndex: 'totalAmount',
            render: (text: Amount) => (
                <span>
                    {text?.amountUpto2dp
                        ? `${text.currency} ${commaSeperator(parseFloat(String(text.amountUpto2dp)).toFixed(2))}`
                        : currency
                        ? `${currency} 0.00`
                        : ''}
                </span>
            ),
            sorter: (a: Cashflow, b: Cashflow) =>
                (a.totalAmount?.amountUpto2dp || 0) - (b.totalAmount?.amountUpto2dp || 0),
        },
        {
            title: 'Management Fees',
            width: 200,
            dataIndex: 'managementFee',
            render: (text: Amount) => (
                <span>
                    {text?.amountUpto2dp
                        ? `${text.currency} ${commaSeperator(parseFloat(String(text.amountUpto2dp)).toFixed(2))}`
                        : currency
                        ? `${currency} 0.00`
                        : ''}
                </span>
            ),
            sorter: (a: Cashflow, b: Cashflow) =>
                (a.managementFee?.amountUpto2dp || 0) - (b.managementFee?.amountUpto2dp || 0),
        },
        {
            title: 'Net Total Payment',
            width: 200,
            dataIndex: 'netPayment',
            render: (text: Amount) => (
                <span>
                    {text?.amountUpto2dp
                        ? `${text.currency} ${commaSeperator(parseFloat(String(text.amountUpto2dp)).toFixed(2))}`
                        : currency
                        ? `${currency} 0.00`
                        : ''}
                </span>
            ),
            sorter: (a: Cashflow, b: Cashflow) =>
                (a.netPayment?.amountUpto2dp || 0) - (b.netPayment?.amountUpto2dp || 0),
        },
    ];
    return (
        <div>
            {(data || []).length > 0 && (
                <HSTable
                    columns={columns}
                    data={data || []}
                    rowKey={(d: Cashflow) => `${d.paymentDate.date}_${d.totalAmount?.amountUpto2dp || 0}`}
                    pagination={{ isShow: true }}
                />
            )}
            {(data || []).length === 0 && (
                <div>
                    <Empty description={`No Repayment Schedule Data`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            )}
        </div>
    );
};

export default RepaymentTableView;
